import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import DateTime from "react-datetime";
import moment from "moment";
import "react-datetime/css/react-datetime.css";
import styles from "./InputDate.module.scss";
import { IoMdClose } from "react-icons/io";

const getDisplayValue = (value, dateFormat) => {
  if (!value) return "";
  const format = dateFormat || "YYYY-MM-DD";
  const parsed = moment.isMoment(value) ? value : moment(value, format);
  return parsed.isValid() ? parsed.format(format) : String(value);
};

// Beberapa halaman laporan menyimpan periode sebagai objek ({ year, month_start })
// alih-alih string/moment; ubah ke bentuk yang bisa dibaca DateTime.
const toDateValue = (value) => {
  if (!value || moment.isMoment(value) || value instanceof Date) return value;
  if (typeof value !== "object") return value;
  const year = value.year ?? value.selected_year;
  if (!year) return "";
  const month = value.month_start ?? value.month ?? value.month_end ?? "01";
  const parsed = moment(`${year}-${month}`, "YYYY-MM");
  return parsed.isValid() ? parsed : "";
};

const InputDate = ({
  label,
  name,
  error,
  isDisabled,
  value,
  onChange,
  onBlur,
  placeholder,
  errorMessage,
  isValidDate,
  dateFormat,
  isRequired,
  isClearable = false,
  ...props
}) => {
  const containerRef = useRef(null);
  const calendarRef = useRef(null);
  const dateValue = toDateValue(value);
  const displayValue = getDisplayValue(dateValue, dateFormat);
  const [inputValue, setInputValue] = useState(displayValue);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({
    left: 0,
    top: 0,
  });

  const inputClasses = [styles.input];
  if (error) {
    inputClasses.push(styles.error);
  }

  const getInputClass = (inputProps) => {
    let classes = [inputProps.className];
    if (isClearable) classes.push(styles.readOnlyInput);
    if (isDisabled) classes.push(styles.disabledInput);
    return classes.join(" ");
  };

  const positionCalendar = useCallback(() => {
    if (!containerRef.current || !calendarRef.current) return;
    const input = containerRef.current.querySelector("input");
    if (!input) return;
    const rect = input.getBoundingClientRect();
    const picker = calendarRef.current.querySelector(".rdtPicker");
    if (!picker) return;

    const pickerRect = picker.getBoundingClientRect();
    const margin = 8;
    const width = pickerRect.width;
    const height = pickerRect.height;
    const left = Math.max(
      margin,
      Math.min(rect.left, window.innerWidth - width - margin),
    );
    const below = rect.bottom + height + margin <= window.innerHeight;
    const preferredTop = below ? rect.bottom : rect.top - height;
    const top = Math.max(
      margin,
      Math.min(preferredTop, window.innerHeight - height - margin),
    );

    setCalendarPosition({ left, top });
  }, []);

  useLayoutEffect(() => {
    if (!isCalendarOpen) return undefined;

    positionCalendar();
    const animationFrame = window.requestAnimationFrame(positionCalendar);
    window.addEventListener("resize", positionCalendar);
    window.addEventListener("scroll", positionCalendar, true);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", positionCalendar);
      window.removeEventListener("scroll", positionCalendar, true);
    };
  }, [isCalendarOpen, positionCalendar]);

  useEffect(() => {
    if (!isCalendarOpen) return undefined;

    const closeOnOutsidePointerDown = (event) => {
      if (
        !containerRef.current?.contains(event.target) &&
        !calendarRef.current?.contains(event.target)
      ) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsidePointerDown);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsidePointerDown);
    };
  }, [isCalendarOpen]);

  useEffect(() => {
    if (!isInputFocused || !displayValue) {
      setInputValue(displayValue);
    }
  }, [displayValue, isInputFocused]);

  const handleInputFocus = () => {
    setIsInputFocused(true);
    setIsCalendarOpen(true);
  };

  const handleInputBlur = (event) => {
    setIsInputFocused(false);
    if (onBlur) onBlur(event);
  };

  const renderInput = (inputProps, openCalendar, closeCalendar) => {
    const openPortalCalendar = () => {
      setIsCalendarOpen(true);
    };

    const handleInputChange = (event) => {
      setInputValue(event.target.value);
      inputProps.onChange(event);
    };

    return (
      <div style={{ position: "relative", width: "100%" }}>
        <input
          {...inputProps}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onClick={openPortalCalendar}
          onFocus={handleInputFocus}
          readOnly={isClearable}
          className={`${getInputClass(inputProps)}`}
          disabled={isDisabled}
          style={{
            width: "100%",
            paddingRight: isClearable ? "30px" : "8px",
          }}
        />
        {isClearable && dateValue && (
          <button
            type="button"
            onClick={() => {
              onChange(null);
              closeCalendar();
              setIsCalendarOpen(false);
            }}
            className={styles.clearButton}
          >
            <IoMdClose size={16} color="#888" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={styles.formContainer} ref={containerRef}>
      {label && (
        <label htmlFor={label}>
          {label} {isRequired ? <sup style={{ color: "#D91919" }}>*</sup> : null}{" "}
        </label>
      )}
      <DateTime
        name={name}
        timeFormat={false}
        dateFormat={dateFormat}
        open={false}
        closeOnClickOutside={true}
        closeOnSelect={true}
        className={inputClasses.join(" ")}
        value={dateValue}
        onChange={onChange}
        onBlur={onBlur}
        renderInput={renderInput}
        inputProps={{
          placeholder: placeholder,
          name: name,
          autoComplete: "off",
          disabled: isDisabled,
        }}
        isValidDate={isValidDate}
        {...props}
      />
      {error && <span className={styles.invalidInput}>{errorMessage}</span>}
      {isCalendarOpen &&
        createPortal(
          <div
            ref={calendarRef}
            className="input-date-calendar-portal"
            style={calendarPosition}
          >
            <DateTime
              input={false}
              timeFormat={false}
              dateFormat={dateFormat}
              value={dateValue}
              closeOnSelect={true}
              onChange={(nextValue) => {
                onChange(nextValue);
                setIsCalendarOpen(false);
              }}
              isValidDate={isValidDate}
              {...props}
            />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default InputDate;
